import React from 'react';
import {
  ScaleIcon,
  ClockIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ChartBarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';

const DisputeStatisticsCards = ({ statistics = {} }) => {
  // Use provided statistics or fallback to defaults
  const defaultStats = {
    active_cases: 0,
    pending_cases: 0,
    settled_cases: 0,
    escalated_cases: 0,
    scheduled_hearings: 0,
    resolution_rate: 0,
    avg_resolution_days: 0,
    this_month: {
      active_change: 0,
      pending_change: 0,
      settled_change: 0,
      escalated_change: 0,
      hearings_change: 0,
      new_cases: 0,
      resolved: 0
    }
  };

  // Format numbers for better display
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toString();
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return '0%';
    return `${num}%`;
  };

  const stats = [
    // {
    //   title:      'Total Active Cases',
    //   value:      formatNumber(statistics.active_cases),
    //   icon:       <ScaleIcon className="w-7 h-7 text-blue-600" />,
    //   change:     statistics.this_month?.active_change > 0 ? `+${statistics.this_month?.active_change}` : statistics.this_month?.active_change?.toString() || '0',
    //   changeType: (statistics.this_month?.active_change || 0) >= 0 ? 'increase' : 'decrease',
    //   bgColor:    'bg-blue-50',
    //   textColor:  'text-blue-600',
    //   subtitle:   'Current open cases'
    // },
    // {
    //   title:      'Pending Cases',
    //   value:      formatNumber(statistics.pending_cases),
    //   icon:       <ClockIcon className="w-7 h-7 text-yellow-600" />,
    //   change:     statistics.this_month?.pending_change > 0 ? `+${statistics.this_month?.pending_change}` : statistics.this_month?.pending_change?.toString() || '0',
    //   changeType: (statistics.this_month?.pending_change || 0) >= 0 ? 'increase' : 'decrease',
    //   bgColor:    'bg-yellow-50',
    //   textColor:  'text-yellow-600',
    //   subtitle:   'Awaiting action'
    // },
    {
      title:      'Settled Cases',
      value:      formatNumber(statistics.settled_cases),
      icon:       <DocumentCheckIcon className="w-7 h-7 text-green-600" />,
      change:     statistics.this_month?.settled_change > 0 ? `+${statistics.this_month?.settled_change}` : statistics.this_month?.settled_change?.toString() || '0',
      changeType: (statistics.this_month?.settled_change || 0) >= 0 ? 'increase' : 'decrease',
      bgColor:    'bg-green-50',
      textColor:  'text-green-600',
      subtitle:   'Successfully resolved'
    },
    {
      title:      'Escalated Cases',
      value:      formatNumber(statistics.escalated_cases),
      icon:       <ExclamationTriangleIcon className="w-7 h-7 text-red-600" />,
      change:     statistics.this_month?.escalated_change > 0 ? `+${statistics.this_month?.escalated_change}` : statistics.this_month?.escalated_change?.toString() || '0',
      changeType: (statistics.this_month?.escalated_change || 0) >= 0 ? 'increase' : 'decrease',
      bgColor:    'bg-red-50',
      textColor:  'text-red-600',
      subtitle:   'Moved to higher courts'
    },
    {
      title:      'Scheduled Hearings',
      value:      formatNumber(statistics.scheduled_hearings),
      icon:       <CalendarIcon className="w-7 h-7 text-purple-600" />,
      change:     statistics.this_month?.hearings_change > 0
                 ? `+${statistics.this_month?.hearings_change}`
                 : (statistics.this_month?.hearings_change || 0).toString(),
      changeType: (statistics.this_month?.hearings_change || 0) >= 0 ? 'increase' : 'decrease',
      bgColor:    'bg-purple-50',
      textColor:  'text-purple-600',
      subtitle:   'Upcoming proceedings'
    },
    // {
    //   title:      'New Cases This Month',
    //   value:      formatNumber(statistics.this_month?.new_cases),
    //   icon:       <ChartBarIcon className="w-7 h-7 text-indigo-600" />,
    //   change:     '',
    //   changeType: 'none',
    //   bgColor:    'bg-indigo-50',
    //   textColor:  'text-indigo-600',
    //   subtitle:   'August 2025'
    // },
    {
      title:      'Resolution Rate',
      value:      formatPercentage(statistics.resolution_rate),
      icon:       <ArrowTrendingUpIcon className="w-7 h-7 text-emerald-600" />,
      change:     '',
      changeType: 'none',
      bgColor:    'bg-emerald-50',
      textColor:  'text-emerald-600',
      subtitle:   'Case resolution efficiency'
    },
    {
      title:      'Average Resolution Days',
      value:      formatNumber(statistics.avg_resolution_days),
      icon:       <ClipboardDocumentCheckIcon className="w-7 h-7 text-cyan-600" />,
      change:     '',
      changeType: 'none',
      bgColor:    'bg-cyan-50',
      textColor:  'text-cyan-600',
      subtitle:   'Days to resolve cases'
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      {stats.map((stat, index) => (
        <div
          key={index}
          className={`${stat.bgColor} p-4 rounded-xl shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow duration-300`}
        >
          <div className="flex justify-between items-start">
            <div className="flex justify-start items-center gap-3">
              <div className="rounded-full p-2 bg-white bg-opacity-70">
                {stat.icon}
              </div>
              <div className={`text-xl font-bold ${stat.textColor}`}>{stat.value}</div>
            </div>
            {stat.changeType !== 'none' && (
              <div
                className={`text-xs font-medium ${
                  stat.changeType === 'increase'
                    ? 'text-green-600 bg-green-100'
                    : 'text-red-600 bg-red-100'
                } px-2 py-1 rounded-full`}
              >
                {stat.change} {stat.changeType === 'increase' ? '↑' : '↓'}
              </div>
            )}
          </div>
          <div className="mt-2">
            <p className="text-gray-600 text-sm font-medium">{stat.title}</p>

            <p className="text-xs text-gray-500 mt-1">{stat.subtitle}</p>
          </div>
        </div>
      ))}
    </div>
  );
};



export default React.memo(DisputeStatisticsCards);
